import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { apiGet, apiPost, apiDelete } from "../lib/api";
import QuestionView from "../components/QuestionView";
import MotionButton from "../components/motion/MotionButton";
import MotionCard from "../components/motion/MotionCard";
import { Loader2, Brain, CheckCircle2, Clock, Trophy } from "lucide-react";

const BATCH_SIZE = 10;

function StatCard({ testId, icon, label, value, accentBorder = "border-brand-border" }) {
  return (
    <MotionCard
      testId={testId}
      hoverLift={false}
      className={`p-4 ${accentBorder} border`}
    >
      <div className="flex items-center gap-2 eyebrow">
        {icon} {label}
      </div>
      <div className="font-serif text-3xl font-semibold mt-2 text-brand-primary leading-none">{value}</div>
    </MotionCard>
  );
}

function IdleState({ stats, onStart, formatNextDue }) {
  if (!stats || stats.total_tracked === 0) {
    return (
      <>
        <p className="text-base text-brand-secondary leading-relaxed max-w-md mx-auto">
          Nessuna domanda tracciata ancora. Completa qualche allenamento per iniziare
          a costruire la tua memoria a lungo termine.
        </p>
        <Link to="/training">
          <MotionButton data-testid="srs-go-training-btn" variant="primary" className="mt-6">
            Vai all'allenamento
          </MotionButton>
        </Link>
      </>
    );
  }
  if (stats.due_now === 0) {
    return (
      <>
        <CheckCircle2 className="h-12 w-12 text-brand-green mx-auto mb-3" />
        <h3 className="font-serif text-2xl font-semibold text-brand-primary">Tutto sotto controllo!</h3>
        <p className="text-base text-brand-secondary mt-3 max-w-md mx-auto">
          Nessuna domanda da ripassare ora.
          {stats.next_due_at && <> Prossimo ripasso {formatNextDue(stats.next_due_at)}.</>}
        </p>
      </>
    );
  }
  return (
    <>
      <h3 className="font-serif text-2xl font-semibold text-brand-primary">
        {stats.due_now} {stats.due_now === 1 ? "domanda pronta" : "domande pronte"} per il ripasso
      </h3>
      <p className="text-base text-brand-secondary mt-3 max-w-md mx-auto">
        Avvia una sessione mirata. Ripasseremo {Math.min(BATCH_SIZE, stats.due_now)} domande.
      </p>
      <MotionButton data-testid="srs-start-btn" variant="primary" size="lg" className="mt-6" onClick={onStart}>
        Inizia ripasso
      </MotionButton>
    </>
  );
}

export default function Review() {
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("idle");
  const [reviewed, setReviewed] = useState(0);
  const [favIds, setFavIds] = useState(new Set());
  const startRef = useRef(0);

  const fetchStats = useCallback(async () => {
    const r = await apiGet("/srs/stats");
    setStats(r);
    return r;
  }, []);

  useEffect(() => {
    fetchStats();
    apiGet("/favorites").then((items) => setFavIds(new Set(items.map((i) => i.question_id))));
  }, [fetchStats]);

  const startReview = useCallback(async () => {
    setPhase("loading");
    const r = await apiGet("/srs/due", { limit: BATCH_SIZE });
    if (!r.questions || r.questions.length === 0) { setPhase("empty"); return; }
    setQueue(r.questions); setIdx(0); setReviewed(0); setPhase("reviewing");
    startRef.current = Date.now();
  }, []);

  const onAnswered = useCallback(() => setReviewed((n) => n + 1), []);

  const onNext = useCallback(async () => {
    const nextIdx = idx + 1;
    if (nextIdx >= queue.length) { setPhase("done"); await fetchStats(); return; }
    setIdx(nextIdx); startRef.current = Date.now();
  }, [idx, queue.length, fetchStats]);

  const toggleFav = async (qid, want) => {
    if (want) { await apiPost(`/favorites/${qid}`); setFavIds((p) => new Set([...p, qid])); }
    else { await apiDelete(`/favorites/${qid}`); setFavIds((p) => { const n = new Set(p); n.delete(qid); return n; }); }
  };

  const restart = useCallback(async () => {
    setPhase("idle"); setQueue([]); setIdx(0); await fetchStats();
  }, [fetchStats]);

  const formatNextDue = (iso) => {
    if (!iso) return null;
    const diffH = Math.max(0, (new Date(iso) - Date.now()) / 3600000);
    if (diffH < 24) return `tra ${Math.ceil(diffH)} ore`;
    return `tra ${Math.ceil(diffH / 24)} giorni`;
  };

  return (
    <div className="px-6 sm:px-10 py-8 lg:py-12 max-w-4xl mx-auto">
      <div className="mb-8">
        <div className="eyebrow">// Sistema</div>
        <h1 className="heading-display text-4xl sm:text-5xl text-brand-primary mt-1 flex items-center gap-3">
          <Brain className="h-8 w-8 text-brand-green" />
          Ripasso <em className="italic-display">intelligente</em>
        </h1>
        <p className="text-base text-brand-secondary mt-2 leading-relaxed max-w-xl">
          Algoritmo a ripetizione spaziata (SM-2). Ti riproponiamo le domande quando rischi di
          dimenticarle, dando priorità ai punti deboli.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <StatCard testId="srs-stat-due" icon={<Clock className="h-4 w-4 text-brand-gold" />} label="Da ripassare ora" value={stats.due_now} accentBorder="border-brand-gold/40" />
          <StatCard testId="srs-stat-learning" icon={<Brain className="h-4 w-4 text-brand-green" />} label="In apprendimento" value={stats.learning} accentBorder="border-brand-green/40" />
          <StatCard testId="srs-stat-known" icon={<Trophy className="h-4 w-4 text-brand-gold" />} label="Memorizzate" value={stats.known} accentBorder="border-brand-gold/40" />
          <StatCard testId="srs-stat-total" icon={<CheckCircle2 className="h-4 w-4 text-brand-secondary" />} label="Totale tracciate" value={stats.total_tracked} />
        </div>
      )}

      {phase === "idle" && (
        <div className="premium-card p-8 sm:p-10 text-center">
          <IdleState stats={stats} onStart={startReview} formatNextDue={formatNextDue} />
        </div>
      )}

      {phase === "loading" && (
        <div className="premium-card p-12 text-center text-brand-secondary">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        </div>
      )}

      {phase === "empty" && (
        <div className="premium-card p-10 text-center">
          <CheckCircle2 className="h-12 w-12 text-brand-green mx-auto mb-3" />
          <p className="text-base text-brand-primary">Nessuna scheda da ripassare al momento.</p>
        </div>
      )}

      {phase === "reviewing" && queue.length > 0 && (
        <>
          <div data-testid="srs-progress" className="text-sm text-brand-secondary mb-4">
            Domanda {idx + 1} di {queue.length}
          </div>
          <QuestionView
            key={queue[idx].question_id}
            question={queue[idx]}
            mode="training"
            isFavorite={favIds.has(queue[idx].question_id)}
            onToggleFavorite={toggleFav}
            onNext={onNext}
            onAnswered={onAnswered}
          />
        </>
      )}

      {phase === "done" && (
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="border-2 border-brand-green/40 bg-brand-green/5 rounded-2xl p-8 sm:p-10 text-center"
        >
          <Trophy className="h-12 w-12 text-brand-green mx-auto mb-3" />
          <h3 className="heading-display text-3xl text-brand-primary">Sessione completata!</h3>
          <p className="text-base text-brand-secondary mt-3">
            Hai ripassato <span className="text-brand-primary font-semibold">{reviewed}</span>{" "}
            {reviewed === 1 ? "domanda" : "domande"}. Le riproporremo al momento giusto.
          </p>
          <MotionButton data-testid="srs-restart-btn" variant="primary" className="mt-6" onClick={restart}>
            Torna al ripasso
          </MotionButton>
        </motion.div>
      )}
    </div>
  );
}
